import {clsx} from 'clsx'
import {useCallback, useLayoutEffect, useState} from 'react'

import getPositionOnScreen, {type XEdge, type YEdge} from '../../hooks/get-position-on-screen'
import styles from './picker-list.module.css'

export function useAdjustPickerPosition(
  inputRef: React.RefObject<HTMLElement | null>,
  menuRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  deps: Array<any>,
  options?: Partial<{
    xAlign: XEdge
    yAlign: YEdge
  }>,
) {
  const [xAlignMemory, setXAlignMemory] = useState<XEdge | undefined>('left')
  const [yAlignMemory, setYAlignMemory] = useState<YEdge | undefined>('top')

  const adjustPickerPosition = useCallback(() => {
    if (!inputRef?.current || !menuRef.current) return

    const {top, left, xAlign, yAlign, hasContentDimensions} = getPositionOnScreen({
      originRef: inputRef,
      contentRef: menuRef,
      alignment: {
        xAlign: options?.xAlign ?? xAlignMemory,
        yAlign: options?.yAlign ?? yAlignMemory,
        yTopOffset: 0,
      },
    })

    menuRef.current.style.top = `${top}px`
    menuRef.current.style.left = `${left}px`

    if (!xAlignMemory && hasContentDimensions) {
      setXAlignMemory(xAlign)
    }

    if (!yAlignMemory && hasContentDimensions) {
      setYAlignMemory(yAlign)
    }
  }, [inputRef, menuRef, xAlignMemory, yAlignMemory, options])

  useLayoutEffect(() => {
    if (!isOpen) {
      setXAlignMemory(undefined)
      setYAlignMemory(undefined)
    }
    adjustPickerPosition()
    const resizeObserver = new ResizeObserver(adjustPickerPosition)
    resizeObserver.observe(document.documentElement)

    return () => {
      resizeObserver.unobserve(document.documentElement)
      resizeObserver.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustPickerPosition, isOpen, ...deps])

  return {adjustPickerPosition}
}

/**
 * For smaller viewports, ensure that the picker width and height do not extend beyond the viewport
 * The current width determination: calc(100vw - 52px) is determined by the width of the "Add item button" in the omnibar
 * along with some additional pixels to allow for padding.
 */

const OMNIBAR_OFFSET_X = 52
const OMNIBAR_OFFSET_Y = 60

type PickerListProps = {
  offsetX?: number
  offsetY?: number
  ref?: React.Ref<HTMLUListElement>
} & React.HTMLAttributes<HTMLUListElement>

export const PickerList = ({ref, ...props}: PickerListProps) => {
  const {children, className, offsetX, offsetY, ...rest} = props
  return (
    <ul
      role="listbox"
      ref={ref}
      {...rest}
      style={{
        '--picker-list-offset-x': `${offsetX ?? OMNIBAR_OFFSET_X}px`,
        '--picker-list-offset-y': `${offsetY ?? OMNIBAR_OFFSET_Y}px`,
      }}
      className={clsx(styles.pickerListContainer, className)}
    >
      {children}
    </ul>
  )
}

PickerList.displayName = 'PickerList'

type PickerItemProps = {
  children?: React.ReactNode
  ref?: React.Ref<HTMLLIElement>
} & React.HTMLAttributes<HTMLLIElement>

export const PickerItem = ({ref, ...props}: PickerItemProps) => {
  return (
    <li {...props} className={clsx(styles.pickerItem, styles.blueActiveIndicator)} ref={ref}>
      {props.children}
    </li>
  )
}

PickerItem.displayName = 'PickerItem'
